import { faPercent, faBoxOpen } from "@fortawesome/free-solid-svg-icons";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { formatCurrency, formatPercentage } from "utils/formatters";
import { DashCard, DashCardHeader, DashCardBody } from "./v2/DashCard";
import DashEmptyState from "./v2/DashEmptyState";

interface ProdutoMargemVenda {
  descricao: string;
  margem: number;
  faturamento: number;
}

interface ObterTopProdutosPorMargemResponse {
  produtos: ProdutoMargemVenda[];
}

const renderPlaceholderRows = () =>
  Array(5)
    .fill(0)
    .map((_, index) => (
      <tr key={index}>
        <td className="dash-skeleton" style={{ height: 14 }} />
        <td className="dash-skeleton" style={{ height: 14 }} />
        <td className="dash-skeleton" style={{ height: 14 }} />
      </tr>
    ));

const TopProdutosPorMargem = () => {
  const { getQueryRetryHandler } = usePermissionModal();

  const { data, isLoading, error } = useQuery({
    queryKey: ["topProdutosPorMargem"],
    queryFn: async () =>
      (
        await axios.get<ObterTopProdutosPorMargemResponse>(
          "/api/dashboard/vendas/top-produtos-margem",
        )
      ).data,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: getQueryRetryHandler({
      queryKey: ["topProdutosPorMargem"],
      defaultMessage: "Você não possui permissão para visualizar os produtos por margem.",
      showModal: false,
    }),
  });

  const isAccessDenied = axios.isAxiosError(error) && error?.response?.status === 403;
  if (isAccessDenied) return null;

  const produtos = data?.produtos ?? [];

  return (
    <DashCard hoverable={false}>
      <DashCardHeader
        icon={faPercent}
        variant="good"
        title="Top Produtos por Margem"
        subtitle="Evita priorizar o que vende muito mas dá pouco lucro"
      />
      <DashCardBody flush>
        {isLoading ? (
          <table className="dash-table">
            <tbody>{renderPlaceholderRows()}</tbody>
          </table>
        ) : produtos.length === 0 ? (
          <DashEmptyState icon={faBoxOpen} title="Nenhum produto encontrado" description="Não há dados de margem suficientes para o período." />
        ) : (
          <table className="dash-table">
            <thead>
              <tr>
                <th>Produto</th>
                <th className="dash-num">Margem</th>
                <th className="dash-num">Faturamento</th>
              </tr>
            </thead>
            <tbody>
              {produtos.map((produto, index) => (
                <tr key={index}>
                  <td className="dash-table-primary">{produto.descricao}</td>
                  <td className="dash-num">
                    <span className="dash-pill dash-pill--good">{formatPercentage(produto.margem)}</span>
                  </td>
                  <td className="dash-num">{formatCurrency(produto.faturamento)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DashCardBody>
    </DashCard>
  );
};

export default TopProdutosPorMargem;
